import {
    AccountPaymentMode,
    AccountPaymentSelection,
    OpenTabAccount,
} from '@/features/openTabs/types'
import {
    getAccountPaymentItems,
    getSelectedPaymentTotal,
    isFullAccountSelection,
} from '@/features/openTabs/utils/accountCalculations'
import { RefObject, useCallback, useMemo, useState } from 'react'

interface UsePaymentSelectionArgs {
    selectedAccount: OpenTabAccount | undefined
    detailLoading: boolean
    detailFailed: boolean
    settleAccountInFlightRef: RefObject<boolean>
}

const usePaymentSelection = ({
    selectedAccount,
    detailLoading,
    detailFailed,
    settleAccountInFlightRef,
}: UsePaymentSelectionArgs) => {
    const [payment, setPayment] = useState<{
        selection: AccountPaymentSelection
        mode: AccountPaymentMode
    }>({ selection: {}, mode: 'split' })
    const { selection: paymentSelection, mode: paymentSelectionMode } = payment
    const clearPaymentSelection = useCallback(
        () => setPayment({ selection: {}, mode: 'split' }),
        []
    )
    const prepareFullPayment = useCallback(() => {
        if (!selectedAccount || detailLoading || detailFailed || settleAccountInFlightRef.current)
            return false

        const selection = Object.fromEntries(
            selectedAccount.orders.flatMap(({ items }) =>
                items.map(({ id, quantity }) => [id, quantity])
            )
        )
        if (!isFullAccountSelection(selectedAccount, selection)) return false

        setPayment({ selection, mode: 'all' })
        return true
    }, [selectedAccount, detailLoading, detailFailed, settleAccountInFlightRef])
    const remainingPaymentItems = useMemo(
        () => getAccountPaymentItems(selectedAccount, paymentSelection, false),
        [selectedAccount, paymentSelection]
    )
    const selectedPaymentItems = useMemo(
        () => getAccountPaymentItems(selectedAccount, paymentSelection, true),
        [selectedAccount, paymentSelection]
    )
    const selectedPaymentTotal = useMemo(
        () => getSelectedPaymentTotal(selectedAccount, paymentSelection),
        [selectedAccount, paymentSelection]
    )
    const moveItemToPayment = useCallback(
        (itemId: string) => {
            if (
                !selectedAccount ||
                selectedAccount.status !== 'open' ||
                detailLoading ||
                detailFailed ||
                settleAccountInFlightRef.current
            )
                return
            const item = selectedAccount.orders
                .flatMap((order) => order.items)
                .find((candidate) => candidate.id === itemId)
            if (!item || !Number.isSafeInteger(item.quantity) || item.quantity <= 0) return

            setPayment((current) => {
                const selection = current.selection
                const selectedQuantity = selection[itemId] ?? 0
                if (selectedQuantity >= item.quantity) return current

                return {
                    selection: { ...selection, [itemId]: selectedQuantity + 1 },
                    mode: 'split',
                }
            })
        },
        [selectedAccount, detailLoading, detailFailed, settleAccountInFlightRef]
    )

    const moveItemFromPayment = useCallback(
        (itemId: string) => {
            if (
                !selectedAccount ||
                selectedAccount.status !== 'open' ||
                detailLoading ||
                detailFailed ||
                settleAccountInFlightRef.current
            )
                return

            setPayment((current) => {
                const selection = current.selection
                const selectedQuantity = selection[itemId] ?? 0
                if (selectedQuantity === 0) return current

                const nextSelection = { ...selection }
                if (selectedQuantity === 1) delete nextSelection[itemId]
                else nextSelection[itemId] = selectedQuantity - 1

                return { selection: nextSelection, mode: 'split' }
            })
        },
        [selectedAccount, detailLoading, detailFailed, settleAccountInFlightRef]
    )

    return useMemo(
        () => ({
            paymentSelection,
            paymentSelectionMode,
            selectedPaymentItems,
            remainingPaymentItems,
            selectedPaymentTotal,
            moveItemToPayment,
            moveItemFromPayment,
            prepareFullPayment,
            clearPaymentSelection,
        }),
        [
            paymentSelection,
            paymentSelectionMode,
            selectedPaymentItems,
            remainingPaymentItems,
            selectedPaymentTotal,
            moveItemToPayment,
            moveItemFromPayment,
            prepareFullPayment,
            clearPaymentSelection,
        ]
    )
}

export default usePaymentSelection
